import { t } from "@impero-com/common/utils/i18n";
import {
    notifyRequestError,
    queryClient,
} from "@impero-com/common/utils/query_client";
import { emitNotification } from "@impero-com/ui";

import {
    useDeleteControlProgramsControlProgramId,
    usePostControlProgramsCopy,
} from "api/generated/control-program/control-program";
import { useDeleteControlsControlId } from "api/generated/controls/controls";
import {
    getLegacyControlProgramGetQueryKey,
    useLegacyControlProgramGetSuspense,
} from "api/legacy/legacy-control-program/legacy-control-program";
import {
    legacyDataSheetGroupings,
    useLegacyDataSheetArchive,
} from "api/legacy/legacy-data-sheet/legacy-data-sheet";

import { isControlTestedError } from "./utils";

const useControlProgramDetailQuery = (controlProgramId: number) =>
    useLegacyControlProgramGetSuspense(controlProgramId, {
        query: {
            select: (response) => response.data,
        },
    });

const invalidateControlProgram = (controlProgramId: number) =>
    queryClient.invalidateQueries({
        queryKey: getLegacyControlProgramGetQueryKey(controlProgramId),
    });

const useDeleteControlProgramMutation = () =>
    useDeleteControlProgramsControlProgramId({
        mutation: {
            onError: (error) => {
                if (isControlTestedError(error)) {
                    emitNotification(
                        "error",
                        t(
                            "ViewControlProgram_Delete_ControlProgram_Failed_ControlIsTested"
                        )
                    );
                } else {
                    notifyRequestError(error);
                }
            },
        },
    });

const useCopyControlProgramMutation = () => usePostControlProgramsCopy();

const useDeleteControlMutation = (controlProgramId: number) =>
    useDeleteControlsControlId({
        mutation: {
            onSuccess: () => {
                void invalidateControlProgram(controlProgramId);
            },
            onError: (error) => {
                if (isControlTestedError(error)) {
                    emitNotification(
                        "error",
                        t(
                            "ViewControlProgram_Delete_Control_Failed_ControlIsTested"
                        )
                    );
                } else {
                    notifyRequestError(error);
                }
            },
        },
    });

const useArchiveDataSheetMutation = (controlProgramId: number) =>
    useLegacyDataSheetArchive({
        mutation: {
            onSuccess: () => {
                void invalidateControlProgram(controlProgramId);
            },
        },
    });

const fetchDataSheetGroupings = async (dataSheetId: string) => {
    const response = await legacyDataSheetGroupings(dataSheetId);
    return response.data;
};

export {
    fetchDataSheetGroupings,
    useArchiveDataSheetMutation,
    useControlProgramDetailQuery,
    useCopyControlProgramMutation,
    useDeleteControlMutation,
    useDeleteControlProgramMutation,
};
